import { yupResolver } from "@hookform/resolvers";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { useForm, useWatch } from "react-hook-form";
import * as yup from "yup";
import { TemplateQuestionOptionsConfiguration } from "../components/company/TemplateQuestionOptionsConfiguration";
import { SettingsIcon, BuildingIcon } from "../components/icons";
import {
  Button,
  Checkbox,
  Container,
  EmptyState,
  H4,
  Input,
  Link,
  Radio,
  RadioGroup,
  ScreenContainer,
  ScreenHeader,
  Select,
  FormEntry,
} from "../components/wombat-ui";
import useUpdateCompany from "../graphql/hooks/mutations/useUpdateCompany";
import { GET_VIEWER } from "../graphql/hooks/queries/useGetViewer";
import useAddNotification from "../hooks/useAddNotification";
import useTranslations from "../hooks/useTranslations";
import useViewer from "../hooks/useViewer";

const LOCALE_OPTIONS = [
  { value: "fr-FR", label: "Francais" },
  { value: "en-US", label: "English" },
];

type UpdateCompanyInput = {
  id: string;
  name: string;
  professionSkillLibrariesAttributes: {
    id: string;
    active: boolean;
  }[];
  questionOptionsConfigurationsAttributes: {
    id: string;
    skill: boolean;
  }[];
  visualIdentity: {
    filename: string;
    data: string;
  };
};

const CompanyConfigurationScreen: React.FC = () => {
  const { company, isCapableOfUpdatingCompanies, locale } = useViewer();
  const [addNotification] = useAddNotification();
  const [selectedLocale, setSelectedLocale] = React.useState(locale);

  const t = useTranslations();

  const CompanyConfigurationSchema = useMemo(
    () =>
      yup
        .object()
        .shape({
          name: yup.string().required(t("forms.errors.required") as string),
        })
        .required(),
    [t]
  );

  const successNotification = useCallback(
    () =>
      addNotification({
        variables: {
          title: t("company.updateSuccess") as string,
          status: "success",
        },
      }),
    []
  );
  const failureNotification = useCallback(
    () =>
      addNotification({
        variables: {
          title: t("company.updateError") as string,
          status: "error",
        },
      }),
    []
  );

  const {
    register,
    setValue,
    getValues,
    handleSubmit,
    errors,
    control,
  } = useForm<UpdateCompanyInput>({
    resolver: yupResolver(CompanyConfigurationSchema),
    defaultValues: {
      id: company.id,
      name: company.name,
      contractsEnabled: company.contractsEnabled.toString(),
      reminderEnabled: company.reminderEnabled.toString(),
      reminderGap: company.reminderGap,
      visualIdentity: "",
      locale: company.locale,
      questionOptionsConfigurationsAttributes: [
        ...company.questionOptionsConfigurations,
      ],
    },
  });

  useEffect(() => {
    register(`visualIdentity.filename`);
    register(`visualIdentity.data`);
  }, []);
  const visualIdentityData = useWatch({
    control,
    name: `visualIdentity.data`,
    defaultValue: "",
  });

  const [updateCompany] = useUpdateCompany({
    refetchQueries: [{ query: GET_VIEWER }],
  });
  const onSubmit = useCallback(
    ({
      name,
      visualIdentity,
      locale,
      contractsEnabled,
      reminderEnabled,
      reminderGap,
      questionOptionsConfigurationsAttributes,
    }) => {
      updateCompany({
        variables: {
          input: {
            id: company.id,
            name,
            locale,
            contractsEnabled: contractsEnabled === "true",
            reminderEnabled: reminderEnabled === "true",
            reminderGap,
            visualIdentity:
              visualIdentity.filename !== "" ? visualIdentity.data : null,
            questionOptionsConfigurationsAttributes: [
              ...questionOptionsConfigurationsAttributes,
            ],
          },
        },
      })
        .then(successNotification)
        .catch(failureNotification);
    },
    [company.id]
  );

  const fileRef = useRef<{ filename: string; data: string }>(null);

  const handleVisualIdentityChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const reader = new FileReader();
      const file = e.target?.files?.[0];
      reader.readAsDataURL(file);

      reader.onload = () => {
        setValue(`visualIdentity.filename`, file.name);
        setValue(`visualIdentity.data`, reader.result);
        fileRef.current = {
          filename: file.name,
          data: reader.result as string,
        };
      };
    },
    []
  );

  useEffect(() => {
    setValue("contractsEnabled", company.contractsEnabled ? "true" : "false");
  }, [company.contractsEnabled]);

  useEffect(() => {
    setValue("reminderEnabled", company.reminderEnabled ? "true" : "false");
  }, [company.reminderEnabled]);

  const reminderSelector = useWatch({
    control: control,
    name: `reminderEnabled`,
  });

  return (
    <ScreenContainer
      error={
        !isCapableOfUpdatingCompanies ? (
          <EmptyState
            title={t("network.unauthorized") as string}
            action={
              <Link variant="button" to="/">
                {t("network.backToHome")}
              </Link>
            }
          />
        ) : undefined
      }
    >
      <ScreenHeader
        icon={SettingsIcon}
        title={t("company.header")}
        actions={[
          <Button
            key="submit"
            type="submit"
            variant="primary"
            form="updateCompanyConfiguration"
          >
            {t("forms.actions.send")}
          </Button>,
        ]}
      />
      <Container>
        <form
          className="flex flex-col space-y-8"
          id="updateCompanyConfiguration"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="grid grid-cols-1 col-span-4 mt-6 space-x-4 space-y-6 sm:grid-cols-6">
            <div className="sm:col-span-3">
              <Input
                id="name"
                label={t("company.name") as string}
                error={errors?.name}
                name={`name`}
                defaultValue={company.name}
                ref={register({ required: true })}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-dark-100">
              {t("company.visualIdentity")}
            </label>
            <div className="flex items-center mt-2">
              {visualIdentityData !== "" ? (
                <img
                  className="object-contain w-20 h-20 rounded-full"
                  src={visualIdentityData}
                  alt={`Company visualIdentity`}
                />
              ) : (
                <span className="flex items-center justify-center w-20 h-20 overflow-hidden bg-gray-100 rounded-full">
                  {company.visualIdentity ? (
                    <img
                      className="object-contain w-20 h-20 rounded-full"
                      src={company.visualIdentity}
                      alt={`Company visualIdentity`}
                    />
                  ) : (
                    <BuildingIcon iconClassName="object-contain w-10 h-10 text-gray-300" />
                  )}
                </span>
              )}
              <span className="relative ml-5 overflow-hidden rounded-md shadow-sm">
                <button
                  type="button"
                  data-test-id="companyVisualIdentityButton"
                  className="px-3 py-2 text-sm font-medium leading-4 text-gray-700 transition duration-150 ease-in-out border border-gray-300 rounded-md hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800"
                >
                  {t("forms.actions.change")}
                </button>
                <input
                  className="absolute top-0 right-0 block py-2 opacity-0 cursor-pointer"
                  type="file"
                  onChange={handleVisualIdentityChange}
                  accept="image/png, image/jpeg, image/jpg"
                  name="visual_identity"
                />
              </span>
            </div>
          </div>
          <RadioGroup
            title={t("company.defaultUsersLocale")}
            description={t("company.defaultUsersLocaleDescription")}
          >
            {LOCALE_OPTIONS.map(({ label, value }) => (
              <Radio
                key={value}
                id={`locale-${value}`}
                label={label}
                name="locale"
                value={value}
                ref={register}
              />
            ))}
          </RadioGroup>

          <RadioGroup
            title={t("company.contractsEnabledLabel")}
            description={t("company.contractsEnabledDescription")}
          >
            <Radio
              id={`contract-true`}
              label={t("forms.actions.yes")}
              name="contractsEnabled"
              value={"true"}
              ref={register}
            />
            <Radio
              id={`contract-false`}
              label={t("forms.actions.no")}
              name="contractsEnabled"
              value={"false"}
              ref={register}
            />
          </RadioGroup>

          <RadioGroup
            title={t("company.reminderEnabledLabel")}
            description={t("company.reminderEnabledDescription")}
          >
            <Radio
              id={`reminder-true`}
              label={t("forms.actions.yes")}
              name="reminderEnabled"
              value={"true"}
              ref={register}
            />
            <Radio
              id={`reminder-false`}
              label={t("forms.actions.no")}
              name="reminderEnabled"
              value={"false"}
              ref={register}
            />
          </RadioGroup>

          {(reminderSelector === "true") && (
            <Input
            label={t("company.reminderGapLabel") as string}
            type="number"
            size="small"
            min={1}
            id="reminderGap"
            name="reminderGap"
            ref={register({ valueAsNumber: true })}
            defaultValue={company.reminderGap || undefined}
          />
          )}

          <div>
            <div className="flex items-center pb-4">
              <div className="flex justify-between flex-1">
                <div>
                  <H4>{t("company.questionOptionsConfigTitle")}</H4>
                  <p className="text-xs text-gray-500">
                    {t("company.questionOptionsConfigDescription")}
                  </p>
                </div>
                <Select
                  id="localeSelect"
                  label=""
                  defaultValue={selectedLocale}
                  getValue={(e) => e.value}
                  onChange={(e) => setSelectedLocale(e.value)}
                  items={LOCALE_OPTIONS}
                  renderItem={(e) => e.label}
                />
              </div>
            </div>
            <TemplateQuestionOptionsConfiguration
              register={register}
              control={control}
              selectedLocale={selectedLocale}
            />
          </div>
        </form>
      </Container>
    </ScreenContainer>
  );
};

export default CompanyConfigurationScreen;
